/* 1. Fetch and display related products */
(() => {
  if (customElements.get('cart-related-upsells')) return;

  const escapeHtml = (value = '') => {
    const element = document.createElement('div');
    element.textContent = String(value);
    return element.innerHTML;
  };

  const formatMoney = (amountInCents) => {
    const locale = document.documentElement.lang || 'en-US';
    const currency = window.Shopify?.currency?.active || 'USD';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(Number(amountInCents) / 100);
  };

  class CartRelatedUpsells extends HTMLElement {
    connectedCallback() {
      this.loadRecommendations();
    }

    async loadRecommendations() {
      if (
        this.dataset.loading === 'true' ||
        this.dataset.loaded === 'true'
      ) {
        return;
      }

      const productId = this.dataset.productId;
      const list = this.querySelector('[data-related-upsell-list]');

      if (!productId || !list) return;

      this.dataset.loading = 'true';

      try {
        const endpoint = new URL(
          `${window.Shopify.routes.root}recommendations/products.json`,
          window.location.origin,
        );

        endpoint.searchParams.set('product_id', productId);
        endpoint.searchParams.set('limit', '6');
        endpoint.searchParams.set('intent', 'related');

        const response = await fetch(endpoint.toString(), {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(
            `Recommendations request failed: ${response.status}`,
          );
        }

        const result = await response.json();

        const products = Array.isArray(result.products)
          ? result.products
          : [];

        const cartProductIds = new Set(
          (this.dataset.cartProductIds || '')
            .split(',')
            .filter(Boolean),
        );

        const cards = products
          .filter(
            (recommendedProduct) =>
              !cartProductIds.has(String(recommendedProduct.id)),
          )
          .map((recommendedProduct) => {
            const variant = recommendedProduct.variants?.find(
              (item) => item.available,
            );

            if (!variant) return '';

            const imageData =
              recommendedProduct.featured_image ||
              recommendedProduct.images?.[0];

            const imageUrl =
              typeof imageData === 'string'
                ? imageData
                : imageData?.src || '';

            const productUrl =
              recommendedProduct.url ||
              `${window.Shopify.routes.root}products/${recommendedProduct.handle}`;

            return `
              <article class="cart-upsell-card">
                <a
                  href="${escapeHtml(productUrl)}"
                  class="cart-upsell-card__image-wrapper"
                >
                  ${
                    imageUrl
                      ? `
                        <img
                          src="${escapeHtml(imageUrl)}"
                          alt="${escapeHtml(recommendedProduct.title)}"
                          class="cart-upsell-card__image"
                          loading="lazy"
                          width="350"
                          height="350"
                        >
                      `
                      : ''
                  }
                </a>

                <div class="cart-upsell-card__content">
                  <a
                    href="${escapeHtml(productUrl)}"
                    class="cart-upsell-card__title"
                  >
                    ${escapeHtml(recommendedProduct.title)}
                  </a>

                  <div class="cart-upsell-card__price">
                    ${formatMoney(variant.price)}
                  </div>

                  <button
                    type="button"
                    class="cart-upsell-card__button"
                    data-cart-upsell-add
                    data-variant-id="${variant.id}"
                  >
                    + Add
                  </button>
                </div>
              </article>
            `;
          })
          .filter(Boolean);

        if (!cards.length) {
          this.remove();
          return;
        }

        list.innerHTML = cards.join('');
        this.hidden = false;
        this.dataset.loaded = 'true';
      } catch (error) {
        console.error(
          'Unable to load related cart products:',
          error,
        );

        this.remove();
      } finally {
        this.dataset.loading = 'false';
      }
    }
  }

  customElements.define(
    'cart-related-upsells',
    CartRelatedUpsells,
  );
})();


/* 2. Keep your existing AJAX Add button code */
(() => {
  if (window.cartUpsellsInitialised) return;
  window.cartUpsellsInitialised = true;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-upsell-add]');

    if (!button) return;

    const variantId = Number(button.dataset.variantId);

    if (!variantId || button.disabled) return;

    const originalText = button.textContent;

    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const cartDrawer = document.querySelector('cart-drawer');

      const sectionIds =
        cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [
          'cart-drawer',
          'cart-icon-bubble',
        ];
      const body = new URLSearchParams({
          id: String(variantId),
          quantity: "1",
          sections: sectionIds.join(","),
          sections_url: window.location.pathname,
        });
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body
      });

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(
          result.description || 'The product could not be added.',
        );
      }

      if (cartDrawer?.renderContents) {
        cartDrawer.renderContents(result);
        cartDrawer.open?.();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Cart upsell error:', error);

      button.disabled = false;
      button.textContent = 'Try again';
    } finally {
      window.setTimeout(() => {
        if (document.body.contains(button)) {
          button.disabled = false;
          button.textContent = originalText;
        }
      }, 1500);
    }
  });
})();

/*==========================================
Upsell slider arrows
==========================================*/

(() => {
  if (window.cartUpsellSliderInitialized) return;

  window.cartUpsellSliderInitialized = true;

  const getScrollAmount = (list) => {
    const firstCard = list.querySelector('.cart-upsell-card');

    if (!firstCard) {
      return list.clientWidth * 0.8;
    }

    const listStyles = window.getComputedStyle(list);
    const gap =
      parseFloat(listStyles.columnGap) ||
      parseFloat(listStyles.gap) ||
      0;

    return firstCard.getBoundingClientRect().width + gap;
  };

  const updateArrowStates = (section) => {
    const list = section.querySelector(
      '.cart-drawer-upsells__list',
    );

    const previousButton = section.querySelector(
      '[data-upsell-previous]',
    );

    const nextButton = section.querySelector(
      '[data-upsell-next]',
    );

    if (!list || !previousButton || !nextButton) return;

    const maximumScrollLeft =
      list.scrollWidth - list.clientWidth;

    previousButton.disabled = list.scrollLeft <= 2;

    nextButton.disabled =
      list.scrollLeft >= maximumScrollLeft - 2;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(
      '[data-upsell-previous], [data-upsell-next]',
    );

    if (!button) return;

    const section = button.closest('.cart-drawer-upsells');
    const list = section?.querySelector(
      '.cart-drawer-upsells__list',
    );

    if (!section || !list) return;

    const direction = button.matches('[data-upsell-next]')
      ? 1
      : -1;

    list.scrollBy({
      left: getScrollAmount(list) * direction,
      behavior: 'smooth',
    });

    window.setTimeout(() => {
      updateArrowStates(section);
    }, 400);
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const list = event.target.closest?.(
        '.cart-drawer-upsells__list',
      );

      if (!list) return;

      const section = list.closest('.cart-drawer-upsells');

      if (section) {
        updateArrowStates(section);
      }
    },
    true,
  );

  const initializeSliders = () => {
    document
      .querySelectorAll('.cart-drawer-upsells')
      .forEach(updateArrowStates);
  };

  document.addEventListener(
    'shopify:section:load',
    initializeSliders,
  );

  document.addEventListener(
    'DOMContentLoaded',
    initializeSliders,
  );

  const observer = new MutationObserver(() => {
    initializeSliders();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();